import type {
  ReceiveConfirmLineDto,
  ReceiveConvoyConfirmRequest,
  ReceiveConvoyContextDto,
  ReceiveConvoyLocationDto,
  ReceiveConvoyShipmentDto
} from '@/entities/receive';
import { isLineEmpty } from '@/features/receive-confirm-arrival';

import type { ReceiveConvoyFormValues, ReceiveConvoyLineValues, ReceiveConvoyShipmentValues } from '../model/types';

/** Trims free text and drops it entirely when nothing is left. */
const optionalText = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/** A storage area is preselected only when the location offers exactly one. */
export const defaultStorageArea = (location: ReceiveConvoyLocationDto | undefined): number | null =>
  location?.storageAreas.length === 1 ? location.storageAreas[0].seqno : null;

export const buildConvoyLineDefaults = (
  line: ReceiveConfirmLineDto,
  targetLocationId: string,
  location: ReceiveConvoyLocationDto | undefined
): ReceiveConvoyLineValues => ({
  lineId: line.lineId,
  goodQty: line.expectedGoodQty,
  damagedQty: line.expectedDamagedQty,
  lostQty: 0,
  targetLocationId,
  targetLocationSpaceSeq: defaultStorageArea(location)
});

const buildShipmentDefaults = (
  shipment: ReceiveConvoyShipmentDto,
  locationsById: Map<string, ReceiveConvoyLocationDto>
): ReceiveConvoyShipmentValues => {
  const targetLocationId = shipment.defaultTargetLocationId ?? '';
  const location = locationsById.get(targetLocationId);
  return {
    shipmentId: shipment.shipmentId,
    comments: '',
    transportDocNumber: shipment.defaultTransportDocNumber ?? '',
    lines: shipment.lines.map((line) => buildConvoyLineDefaults(line, targetLocationId, location))
  };
};

/**
 * Form defaults from a freshly fetched context: Good and Damaged prefilled with
 * the outstanding expectation (a full receipt is one click, as in legacy), every
 * line at its shipment's destination, the date on `maxReceiveDate` (today).
 */
export const buildConvoyFormDefaults = (context: ReceiveConvoyContextDto): ReceiveConvoyFormValues => {
  const locationsById = new Map(context.receiveLocations.map((location) => [location.locationId, location]));
  return {
    actualDateOfReceipt: context.maxReceiveDate,
    explanationOfLosses: '',
    printGoodsReceivedNote: false,
    shipments: context.shipments.map((shipment) => buildShipmentDefaults(shipment, locationsById))
  };
};

/**
 * Form values → request body.
 *
 * All-zero lines are dropped, and a truck left with no line is dropped with
 * them: the backend would skip both, and the body then says exactly what was
 * received. A storage area goes out only when it belongs to the line's location.
 */
export const buildConvoyConfirmBody = (
  values: ReceiveConvoyFormValues,
  context: ReceiveConvoyContextDto
): ReceiveConvoyConfirmRequest => {
  const locationsById = new Map(context.receiveLocations.map((location) => [location.locationId, location]));

  const shipments = values.shipments.flatMap((shipment) => {
    const lines = shipment.lines
      .filter((line) => !isLineEmpty(line))
      .map(({ lineId, targetLocationId, targetLocationSpaceSeq, goodQty, damagedQty, lostQty }) => {
        const areaExists = locationsById
          .get(targetLocationId)
          ?.storageAreas.some((area) => area.seqno === targetLocationSpaceSeq);
        return {
          lineId,
          targetLocationId,
          ...(targetLocationSpaceSeq != null && areaExists ? { targetLocationSpaceSeq } : {}),
          goodQty,
          damagedQty,
          lostQty
        };
      });

    if (lines.length === 0) return [];
    return [
      {
        shipmentId: shipment.shipmentId,
        comments: optionalText(shipment.comments),
        transportDocNumber: optionalText(shipment.transportDocNumber),
        lines
      }
    ];
  });

  const hasLoss = shipments.some((shipment) => shipment.lines.some((line) => line.lostQty > 0));

  return {
    actualDateOfReceipt: values.actualDateOfReceipt,
    // Only meaningful alongside a loss; the backend requires it exactly then.
    explanationOfLosses: hasLoss ? optionalText(values.explanationOfLosses) : undefined,
    printGoodsReceivedNote: values.printGoodsReceivedNote,
    shipments
  };
};
